import { t } from '../i18nBridge';
import type { Graph, Step, StepType } from '../model';
import { allEdges, incoming, Port, portsOf, setTarget, targetOf, TRIGGER } from './ports';

/**
 * Graph operations of the journey editor (docs/pilot/SPEC.md §12.4). Pure functions without React or DOM: each takes
 * a graph and returns a NEW graph (the input is never mutated), so undo/redo is a stack of snapshots. Cycles and
 * self-loops are refused here, before the server validator sees the graph. Tested from apps/api
 * (test/journey-editor-ops.spec.ts) against the server's own validateGraph.
 */

export const STEP_ID_RE = /^[a-z0-9_]{1,40}$/;
export const CYCLE_MESSAGE = 'Сценарий не может возвращаться назад: шаги идут только вперёд';
export const MAX_STEPS = 100;

export type OpError = 'cycle' | 'self' | 'unknown' | 'limit';
export const OP_ERROR_TEXT: Record<OpError, string> = {
  get cycle() {
    return t(CYCLE_MESSAGE);
  },
  get self() {
    return t(CYCLE_MESSAGE);
  },
  get unknown() {
    return t('Такого шага нет');
  },
  get limit() {
    return t('В сценарии не больше {n} шагов', { n: MAX_STEPS });
  },
};
export const isOpError = (r: unknown): r is { error: OpError } => !!r && typeof r === 'object' && 'error' in r;

export const cloneGraph = (g: Graph): Graph => JSON.parse(JSON.stringify(g)) as Graph;

/** Defaults of a new step (§12.4). A new email has no template yet: the validator highlights it until one is chosen. */
export function defaultStep(type: StepType): Step {
  switch (type) {
    case 'wait':
      return { type, duration: { hours: 1 }, next: null };
    case 'wait_for_event':
      return { type, event: '', timeout: { days: 1 }, on_event: null, on_timeout: null };
    case 'branch':
      return { type, conditions: [{ when: { consent: 'marketing', status: 'granted' }, next: null }], else: null };
    case 'split':
      return { type, variants: [{ id: 'a', weight: 50, next: null }, { id: 'b', weight: 50, next: null }] };
    case 'send_email':
      return { type, template: { alias: '', version: 'latest' }, variables: {}, next: null };
    case 'send_notification':
      return { type, template: { key: '', version: 'latest' }, policy: { key: '', version: 'latest' }, variables: {}, next: null };
    case 'webhook':
      return { type, endpoint_id: '', include_attributes: [], next: null };
    case 'update_contact':
      return { type, set: {}, next: null };
    case 'digest':
      return { type, event: '', window: { hours: 1 }, max_items: 10, item_template: '{{name}}', next: null };
    case 'exit':
      return { type, reason: 'done' };
  }
}

/** `${type}_${n}` that is not taken. */
export function newStepId(graph: Graph, type: StepType): string {
  for (let n = 1; n < 10_000; n++) {
    const id = `${type}_${n}`;
    if (!graph.steps[id]) return id;
  }
  return `${type}_${Date.now()}`.slice(0, 40);
}

/** Steps reachable from `from` (inclusive) by edges. */
export function reachable(graph: Graph, from: string): Set<string> {
  const seen = new Set<string>();
  const stack = [from];
  while (stack.length) {
    const id = stack.pop()!;
    if (seen.has(id) || !graph.steps[id]) continue;
    seen.add(id);
    for (const key of portsOf(graph.steps[id])) {
      const t = targetOf(graph, { step: id, key });
      if (t && !seen.has(t)) stack.push(t);
    }
  }
  return seen;
}

/** Would `port → target` close a cycle (target already leads back to the port's step)? */
export function wouldCycle(graph: Graph, port: Port, target: string): boolean {
  if (port.step === TRIGGER) return false;
  return reachable(graph, target).has(port.step);
}

/**
 * Adds a step. With `at` the step is inserted into that edge: the port now leads to the new step and every exit of
 * the new step continues to where the port pointed (so the graph stays connected and valid).
 */
export function addStep(graph: Graph, type: StepType, at?: Port): { graph: Graph; id: string } | { error: OpError } {
  if (Object.keys(graph.steps).length >= MAX_STEPS) return { error: 'limit' };
  const g = cloneGraph(graph);
  const id = newStepId(g, type);
  const step = defaultStep(type);
  g.steps[id] = step;
  if (at) {
    const old = targetOf(g, at);
    for (const key of portsOf(step)) setTarget(g, { step: id, key }, old);
    setTarget(g, at, id);
  }
  if (!g.start) g.start = id;
  return { graph: g, id };
}

export function connect(graph: Graph, port: Port, target: string): Graph | { error: OpError } {
  if (!graph.steps[target] || (port.step !== TRIGGER && !graph.steps[port.step])) return { error: 'unknown' };
  if (port.step === target) return { error: 'self' };
  if (wouldCycle(graph, port, target)) return { error: 'cycle' };
  const g = cloneGraph(graph);
  setTarget(g, port, target);
  return g;
}

/** The port leads nowhere: «конец ветки» (for the trigger — no first step, which the validator reports). */
export function disconnect(graph: Graph, port: Port): Graph {
  const g = cloneGraph(graph);
  setTarget(g, port, null);
  return g;
}

/**
 * Removes a step. `heal`: what pointed at it now points at its single exit (if it has exactly one), else at null;
 * `cut`: everything that pointed at it ends there. The start moves to the single exit, else to any remaining step.
 */
export function removeStep(graph: Graph, id: string, mode: 'heal' | 'cut' = 'heal'): Graph {
  if (!graph.steps[id]) return graph;
  const g = cloneGraph(graph);
  const ports = portsOf(g.steps[id]);
  const single = mode === 'heal' && ports.length === 1 ? targetOf(g, { step: id, key: ports[0] }) : null;
  for (const p of incoming(g, id)) if (p.step !== id) setTarget(g, p, single);
  delete g.steps[id];
  if (!g.start || g.start === id || !g.steps[g.start]) g.start = single ?? Object.keys(g.steps)[0] ?? '';
  return g;
}

/** A copy with the same exits (nothing points at it yet, so no cycle can appear). */
export function duplicateStep(graph: Graph, id: string): { graph: Graph; id: string } | { error: OpError } {
  const s = graph.steps[id];
  if (!s) return { error: 'unknown' };
  if (Object.keys(graph.steps).length >= MAX_STEPS) return { error: 'limit' };
  const g = cloneGraph(graph);
  const copy = newStepId(g, s.type);
  g.steps[copy] = JSON.parse(JSON.stringify(s)) as Step;
  return { graph: g, id: copy };
}

/** Renames a step id everywhere (ports, start). Refused if the id is invalid or taken. */
export function renameStep(graph: Graph, id: string, next: string): Graph | { error: OpError } {
  if (!graph.steps[id] || !STEP_ID_RE.test(next) || graph.steps[next]) return { error: 'unknown' };
  const g = cloneGraph(graph);
  const refs = incoming(g, id);
  const steps: Record<string, Step> = {};
  for (const [k, v] of Object.entries(g.steps)) steps[k === id ? next : k] = v;
  g.steps = steps;
  for (const p of refs) setTarget(g, p.step === id ? { ...p, step: next } : p, next);
  if (g.start === id) g.start = next;
  return g;
}

// ---------- branches and splits ----------

export function addBranchCondition(graph: Graph, id: string): Graph {
  const s = graph.steps[id];
  if (s?.type !== 'branch' || s.conditions.length >= 10) return graph;
  const g = cloneGraph(graph);
  (g.steps[id] as Extract<Step, { type: 'branch' }>).conditions.push({ when: { consent: 'marketing', status: 'granted' }, next: null });
  return g;
}

export function removeBranchCondition(graph: Graph, id: string, i: number): Graph {
  const s = graph.steps[id];
  if (s?.type !== 'branch' || s.conditions.length <= 1 || !s.conditions[i]) return graph;
  const g = cloneGraph(graph);
  (g.steps[id] as Extract<Step, { type: 'branch' }>).conditions.splice(i, 1);
  return g;
}

/** Even weights that sum to exactly 100 (the remainder goes to the first variants). */
export function evenWeights(n: number): number[] {
  const base = Math.floor(100 / n);
  return Array.from({ length: n }, (_, i) => base + (i < 100 - base * n ? 1 : 0));
}

const VARIANT_IDS = 'abcdefghijklmnopqrstuvwxyz'.split('');

export function addVariant(graph: Graph, id: string): Graph {
  const s = graph.steps[id];
  if (s?.type !== 'split' || s.variants.length >= 5) return graph;
  const g = cloneGraph(graph);
  const sp = g.steps[id] as Extract<Step, { type: 'split' }>;
  const taken = new Set(sp.variants.map((v) => v.id));
  sp.variants.push({ id: VARIANT_IDS.find((x) => !taken.has(x)) ?? `v${sp.variants.length + 1}`, weight: 0, next: null });
  evenWeights(sp.variants.length).forEach((w, i) => (sp.variants[i].weight = w));
  return g;
}

export function removeVariant(graph: Graph, id: string, i: number): Graph {
  const s = graph.steps[id];
  if (s?.type !== 'split' || s.variants.length <= 2 || !s.variants[i]) return graph;
  const g = cloneGraph(graph);
  const sp = g.steps[id] as Extract<Step, { type: 'split' }>;
  sp.variants.splice(i, 1);
  evenWeights(sp.variants.length).forEach((w, k) => (sp.variants[k].weight = w));
  return g;
}

// ---------- order and insertion ----------

/** Trigger, then steps in reading order (depth-first from the start along ports), then the unreachable ones. */
export function topoOrder(graph: Graph): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  // Longest-path levels give a stable top-down order that respects every edge.
  const level = new Map<string, number>();
  const visit = (id: string | null, depth: number, path: Set<string>) => {
    if (!id || !graph.steps[id] || path.has(id) || (level.get(id) ?? -1) >= depth) return;
    level.set(id, depth);
    path.add(id);
    for (const key of portsOf(graph.steps[id])) visit(targetOf(graph, { step: id, key }), depth + 1, path);
    path.delete(id);
  };
  visit(graph.start, 0, new Set());
  const firstSeen: string[] = [];
  const walk = (id: string | null) => {
    if (!id || !graph.steps[id] || seen.has(id)) return;
    seen.add(id);
    firstSeen.push(id);
    for (const key of portsOf(graph.steps[id])) walk(targetOf(graph, { step: id, key }));
  };
  walk(graph.start);
  const pos = new Map(firstSeen.map((id, i) => [id, i]));
  out.push(...firstSeen.sort((a, b) => level.get(a)! - level.get(b)! || pos.get(a)! - pos.get(b)!));
  for (const id of Object.keys(graph.steps)) if (!seen.has(id)) out.push(id);
  return out;
}

/** Where «вставить после X» goes: X's first free port, else its first port (between X and its target). */
export function insertionPort(graph: Graph, after: string): Port | null {
  if (after === TRIGGER) return { step: TRIGGER, key: 'start' };
  const s = graph.steps[after];
  if (!s) return null;
  const keys = portsOf(s);
  if (!keys.length) return null;
  const free = keys.find((key) => targetOf(graph, { step: after, key }) === null);
  return { step: after, key: free ?? keys[0] };
}

export function insertAfter(graph: Graph, after: string | null, type: StepType): { graph: Graph; id: string; at: Port | null } | { error: OpError } {
  let at: Port | null = null;
  if (after) at = insertionPort(graph, after);
  else if (!Object.keys(graph.steps).length || !graph.start) at = { step: TRIGGER, key: 'start' };
  else {
    // No selection: after the last step of the reading order that still has a free port.
    const order = topoOrder(graph);
    for (let i = order.length - 1; i >= 0 && !at; i--) {
      const p = insertionPort(graph, order[i]);
      if (p && targetOf(graph, p) === null) at = p;
    }
    // Every exit is taken: before the last step that ends a branch (into the edge leading to it).
    for (let i = order.length - 1; i >= 0 && !at; i--) {
      const into = allEdges(graph).find((e) => e.to === order[i]);
      if (into) at = into.port;
    }
  }
  const r = addStep(graph, type, at ?? undefined);
  return isOpError(r) ? r : { ...r, at };
}

/** Steps with nothing pointing at them except the start (shown as «недостижим» by the validator). */
export function unreachableSteps(graph: Graph): string[] {
  const r = graph.start ? reachable(graph, graph.start) : new Set<string>();
  return Object.keys(graph.steps).filter((id) => !r.has(id));
}

/** Key-order-independent JSON (to tell a changed graph from the saved one). */
export function stableJson(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stableJson).join(',')}]`;
  if (v && typeof v === 'object') {
    return `{${Object.keys(v as object)
      .filter((k) => (v as Record<string, unknown>)[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableJson((v as Record<string, unknown>)[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(v);
}

/** The edges as a list (for announcements and the keyboard «connect» mode). */
export const edgesList = allEdges;
